import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MODAL_NAME } from "../../../../constants";
import { ROUTES } from "../../../../constants/routes";
import {
  conversionOfPostDate,
  isPDF,
  renderTextWithLinks,
} from "../../../../helpers";
import { useAppDispatch } from "../../../../hooks/store-hooks";
import {
  ArrowCircleDown,
  BookMark,
  ChatCentredText,
  DeleteBin,
  DeletedContent,
  Dot,
  EditPencil,
  FileText,
  Heart,
  More,
  Siren,
} from "../../../../icons";
import { ChartBar, PinnedIcon } from "../../../../icons/tags-icons";
import {
  useAddBookmarkMutation,
  useAddFeedReactionMutation,
} from "../../../../store/api/homeApi";
import { useUserActionMutation } from "../../../../store/api/userApi";
import { openModal } from "../../../../store/slices/modalSlice";
import PdfViewer from "../../../common/pdf-viewer";
import ProfileImageComp from "../../../common/profile";
import { toastError, toastSuccess } from "../../../toast";
import { tags as tagsWithIcon, TagWithIcon } from "./create-feed";
import ExpandableParagraph from "./expandable-paragraph";
import FeedComments from "./feed-comments";
import ProfileBadgeAvatar from "../../../user/user-profile/profile-avatar";
import VideoPlayer from "../../../common/player/video-player";
import { VerifiedBadgeIcon } from "../../../../icons/badge-icons";
import { ImageContainer } from "../../../common/image-popup/image-modal";

interface FeedCardProps {
  postData: any;
  follow: boolean;
  setIsFollow: (followStatus: boolean) => void;
  selectedFeedType?: string;
  showCommentDefault?: boolean;
  deletedCommentInfo?: { postId: string; commentId: string } | null;
}

export interface PollOption {
  option: string;
  votes: number;
}

const FeedCard: React.FC<FeedCardProps> = ({
  postData,
  follow,
  setIsFollow,
  selectedFeedType,
  showCommentDefault = false,
  deletedCommentInfo,
}) => {
  const dispatch = useAppDispatch();
  const [likeData, setLikeData] = useState({
    like: false,
    count: 0,
  });

  const [addFeedReaction] = useAddFeedReactionMutation();

  const [addBookmark] = useAddBookmarkMutation();

  const [showComment, setShowComment] = useState(showCommentDefault);

  const [commentsCount, setCommentsCount] = useState(0);

  const [pollData, setPollData] = useState<PollOption[]>([]);

  const [totalVotes, setTotalVotes] = useState(0);

  const [timeLeft, setTimeLeft] = useState<string>("");

  const [isExpired, setIsExpired] = useState(false);

  const [isLiking, setIsLiking] = useState(false);

  const [isPinned, setIsPinned] = useState(false);

  const {
    id: postId,
    user,
    postMessage,
    created_at,
    likes,
    liked,
    is_bookmarked,
    attachments,
    comments,
    tags,
    poll,
    already_voted,
    selected_options,
    is_pinned,
  } = postData || {};

  const extension = attachments?.images[0]?.split(".").pop() || "";

  const [hasVoted, setHasVoted] = useState(already_voted);

  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    selected_options || [],
  );

  const [moreOptions, setmoreOptions] = useState(false);

  const [fileDetails, setFileDetails] = useState<{
    name: string;
    extension: string;
  } | null>(null);

  useEffect(() => {
    setLikeData({
      ...likeData,
      like: liked,
      count: likes,
    });
    setCommentsCount(comments || 0);
  }, [liked, comments, poll]);

  useEffect(() => {
    setPollData([]);
    if (poll && poll.options) {
      setPollData(poll.options);
      setTotalVotes(
        poll.options.reduce(
          (sum: number, option: PollOption) => sum + option.votes,
          0,
        ),
      );
    }
    setHasVoted(already_voted);
    setSelectedOptions(selected_options);
    setIsPinned(is_bookmarked || false);
  }, [poll, already_voted, selected_options, is_bookmarked]);

  useEffect(() => {
    setIsExpired(false);
    const time = calculateTimeLeft(poll?.expires_at);
    setTimeLeft(time);
  }, [poll?.expires_at]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setmoreOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [moreOptions]);

  const handleCommets = () => {
    setCommentsCount((prevCount) => prevCount + 1);
  };

  const handleEdit = async () => {
    dispatch(
      openModal({
        modalName: MODAL_NAME.CREATE_POST,
        isModalOpen: true,
        modalData: { ...postData },
      }),
    );
  };

  const handleLike = async () => {
    try {
      setIsLiking(true);
      setLikeData({
        ...likeData,
        like: !likeData.like,
        count: likeData.like ? likeData.count - 1 : likeData.count + 1,
      });
      const response = await addFeedReaction({
        activityId: postId,
        bodyData: { type: "like" },
      }).unwrap();
      if (response.success) {
        setIsLiking(false);
      } else {
        toastError({ message: response?.error?.message });
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      setIsLiking(false);
      setLikeData({
        ...likeData,
        like: !likeData.like,
        count: likeData.like ? likeData.count - 1 : likeData.count + 1,
      });
      toastError({
        messsage: "There was some trouble liking this post, please try again.",
      });
    }
  };

  const handlePollVote = async (
    event: React.MouseEvent<HTMLLabelElement, MouseEvent>,
    option: string,
  ) => {
    event.preventDefault();

    let updatedSelectedOptions = Array.isArray(selectedOptions)
      ? [...selectedOptions]
      : [];
    const isSelected = updatedSelectedOptions?.includes(option);
    if (poll?.allow_multiple) {
      if (isSelected) {
        updatedSelectedOptions = updatedSelectedOptions.filter(
          (selectedOption) => selectedOption !== option,
        );
      } else {
        updatedSelectedOptions.push(option);
      }
    } else {
      updatedSelectedOptions = [option];
    }
    try {
      await addFeedReaction({
        activityId: postId,
        bodyData: {
          type: "vote",
          data: {
            poll_options: updatedSelectedOptions,
          },
        },
      }).unwrap();
      const existingOptions = selectedOptions;
      setSelectedOptions(updatedSelectedOptions);
      const newOptions = updatedSelectedOptions;

      const addedOptions = newOptions.filter(
        (option) => !existingOptions.includes(option),
      );

      const removedOptions = existingOptions.filter(
        (option) => !newOptions.includes(option),
      );
      const updatedPollData = pollData.map((pollOption) => {
        if (addedOptions.includes(pollOption.option)) {
          return { ...pollOption, votes: pollOption.votes + 1 };
        }
        if (
          removedOptions.includes(pollOption.option) &&
          pollOption.votes > 0
        ) {
          return { ...pollOption, votes: pollOption.votes - 1 };
        }

        return pollOption;
      });
      const totalVotes = updatedPollData.reduce((sum, pollOption) => {
        return sum + pollOption.votes;
      }, 0);
      setTotalVotes(totalVotes);
      setPollData(updatedPollData);
      setHasVoted(true);
    } catch (error) {
      console.log("Vote Error", (error as Error).message);
    }
  };

  const [triggerUserAction] = useUserActionMutation();

  const action = async ({ type }: { type: string }) => {
    const userActionResponse = await triggerUserAction({
      user_id: user.id,
      action: type,
    });
    if (userActionResponse.data?.success) {
      setIsFollow(!follow);
    }
  };

  const tagsWithIconMap: { [key: string]: TagWithIcon } = {};
  tagsWithIcon.forEach((tag) => {
    tagsWithIconMap[tag.name] = tag;
  });

  const calculateTimeLeft = (expires_at: string) => {
    const now = moment();
    const expiresAt = moment(expires_at);
    const hoursDiff = expiresAt.diff(now, "hours");

    if (hoursDiff <= 0) {
      setIsExpired(true);
    }

    const displayTime =
      hoursDiff >= 24
        ? `${expiresAt.diff(now, "days")} days`
        : `${hoursDiff} hours`;

    return displayTime;
  };

  const handleBookmark = async () => {
    const pin = isPinned;
    try {
      setIsPinned(!isPinned);
      const response = await addBookmark({
        activityId: postId,
      }).unwrap();
      if (!response.success) {
        setIsPinned(pin);
      }
      toastSuccess({ message: response?.data?.message });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      toastError({
        message:
          "There was some issue in bookmarking this post, please try again",
      });
      setIsPinned(pin);
    }
  };

  const MoreOptions = () => {
    if (postData?.is_deleted) {
      return null;
    }
    return (
      <div className="relative self-start">
        <div
          className="hover:cursor-pointer"
          onClick={() => {
            setmoreOptions(true);
          }}
        >
          <More />
        </div>
        {user?.self ? (
          <div
            ref={wrapperRef}
            className={`absolute z-20 right-0 flex flex-col items-start justify-start bg-white shadow top-5 w-52 rounded-xl ${moreOptions ? "" : "hidden"}`}
          >
            <button
              onClick={() => {
                handleEdit();
              }}
              type="button"
              className="w-full p-4 border-b border-[#ebebeb] justify-start items-center gap-2 inline-flex"
            >
              <EditPencil width={18} height={18} fillColor="#343330" />
              <div className="text-base font-normal leading-tight text-black">
                Edit Post
              </div>
            </button>
            <button
              type="button"
              className="w-full p-4 border-b border-[#ebebeb] justify-start items-center gap-2 inline-flex"
              onClick={() => {
                dispatch(
                  openModal({
                    modalName: MODAL_NAME.DELETE_POST,
                    modalData: postId,
                  }),
                );
                //setModalName(MODAL_NAME.DELETE_POST);
              }}
            >
              <DeleteBin width={18} height={18} />
              <div className="text-base font-normal leading-tight text-black">
                Delete Post
              </div>
            </button>
          </div>
        ) : (
          <div
            ref={wrapperRef}
            className={`absolute z-20 right-0 flex flex-col items-start justify-start bg-white shadow top-5 w-52 rounded-xl ${moreOptions ? "" : "hidden"}`}
          >
            <button
              onClick={() => {
                dispatch(
                  openModal({
                    modalName: MODAL_NAME.REPORT,
                    modalData: postId,
                  }),
                );
              }}
              type="button"
              className="w-full p-4 border-b border-[#ebebeb] justify-start items-center gap-2 inline-flex"
            >
              <Siren />
              <div className="text-base font-normal leading-tight text-black">
                Report Post
              </div>
            </button>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    const fetchDetails = () => {
      if (attachments?.file) {
        const urlParts = attachments?.file?.split("/");
        const fileNameWithExtension = urlParts[urlParts.length - 1];
        const [fullName, extension] = fileNameWithExtension.split(/\.([^.]+)$/);
        const name = fullName.replace(/^\d+[-]?\d*-/, "").trim();
        setFileDetails({
          name,
          extension,
        });
      }
    };
    fetchDetails();
  }, [attachments?.file]);

  return (
    <div className="p-4 bg-white border border-solid rounded-lg border-primary-600 overflow-hidden">
      {(tags && tags?.length > 0) ||
      is_pinned ||
      (poll?.options && poll?.options.length > 0) ? (
        <div className="flex items-center justify-between">
          <div className="flex flex-row gap-2 mb-3 overflow-x-scroll">
            {tags &&
              Array.isArray(tags) &&
              tags?.map(
                (tag: string, index: number) =>
                  tagsWithIconMap[tag] && (
                    <div
                      key={index}
                      className="flex flex-row h-[28px] w-fit gap-1 px-4 py-1 rounded-[24px] text-[13px] mb-2 text-nowrap"
                      style={{
                        color: tagsWithIconMap[tag]?.color || "#1640C1",
                        backgroundColor:
                          tagsWithIconMap[tag]?.backgroundColor || "#E7ECFC",
                      }}
                    >
                      {tagsWithIconMap[tag]?.icon}
                      {tag}
                    </div>
                  ),
              )}
            {poll?.options && poll?.options.length > 0 && (
              <div className="flex flex-row  gap-1 px-4 py-1 bg-[#D1C6FF] rounded-[24px] text-[13px] mb-2 text-[#661EFF]">
                <ChartBar />
                Poll
              </div>
            )}
            {is_pinned && selectedFeedType === "top" && (
              <div className="mb-2 flex items-center px-4 py-1 rounded-[24px] text-[13px] bg-[var(--Gold,#ECD996)]">
                <PinnedIcon width={18} height={18} />
                <span className="ml-1">Pinned post</span>
              </div>
            )}
          </div>
          <MoreOptions />
        </div>
      ) : null}

      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center">
          <Link to={ROUTES.USER_PROFILE.replace(":user_id", user?.id)}>
            <div>
              {user?.badge ? (
                <div className={`relative w-16 h-[76px] mr-2 mt-4`}>
                  <ProfileBadgeAvatar
                    profileImage={user?.profile_picture}
                    customDivClassName="absolute h-14 w-14 border-[4px] z-1"
                    customImgClassName="h-[56px] w-[56px] absolute rounded-full  p-1"
                    customIconDivClassName="absolute z-[11] bottom-3 left-5"
                    customStyle1="w-[50px] h-4  absolute -bottom-2 z-[11] overflow-hidden"
                    customStyle2="w-[200px] h-6  absolute top-[14px] z-10 -left-10 overflow-hidden"
                    customStarWidth={14}
                    customStarHeight={14}
                    customStyle3="w-[48px] h-[48px]"
                    badge={user?.badge}
                  />
                </div>
              ) : (
                <div
                  className={`flex item-center w-12 h-12 mr-4 overflow-hidden rounded-full`}
                >
                  <ProfileImageComp
                    imageUrl={user?.profile_picture}
                    customClass="h-12 w-12"
                    is_opc={user?.memberships?.is_opc}
                  />
                </div>
              )}
            </div>
          </Link>
          <div>
            <Link to={ROUTES.USER_PROFILE.replace(":user_id", user?.id)}>
              <h2 className="text-lg font-semibold text-gray-700 flex items-center">
                {user?.first_name} {user?.last_name}
                {user?.is_verified && (
                  <div className="ml-2">
                    <VerifiedBadgeIcon width={20} height={20} />
                  </div>
                )}
              </h2>
              <div className="w-60 truncate text-primary-500 text-md text-ellipsis">
                {user?.designation}{" "}
                {user?.company_name ? `at ${user.company_name}` : ""}
              </div>
            </Link>
            <div className="text-primary-500 text-md">
              {conversionOfPostDate(created_at)}
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-3 w-fit">
          {user?.self ||
            (follow ? (
              <div
                className="font-semibold cursor-pointer text-[#808080]"
                onClick={() => action({ type: "unfollow" })}
              >
                Following
              </div>
            ) : (
              <div
                className="font-semibold cursor-pointer text-secondary-300"
                onClick={() => action({ type: "follow" })}
              >
                + Follow
              </div>
            ))}
          {(tags && tags?.length > 0) ||
          (poll?.options && poll?.options.length > 0) ||
          is_pinned ? null : (
            <MoreOptions />
          )}
        </div>
      </div>
      {!postData?.is_deleted ? (
        <div className="mb-4">
          {postMessage && (
            <p
              className="font-normal text-primary-900"
              style={{ whiteSpace: "pre-wrap" }}
            >
              <ExpandableParagraph
                text={renderTextWithLinks(postMessage || "")}
                postId={postId}
              />
            </p>
          )}
          {poll?.options && poll?.options.length > 0 && (
            <div className="text-[14px] text-[#808080] flex flex-row items-center gap-x-1">
              <div>{totalVotes ? totalVotes + " votes" : "0 votes "}</div>
              <Dot fillColor="#808080" />
              {<div> {isExpired ? "Expired" : timeLeft}</div>}
            </div>
          )}
        </div>
      ) : (
        <div
          className={`flex gap-2 justify-start items-center ${postData?.is_admin_deleted ? "bg-[#fdeded]" : "bg-primary-300"} p-4 rounded-xl`}
        >
          {postData?.is_admin_deleted ? (
            <DeletedContent />
          ) : (
            <DeleteBin width={32} height={32} />
          )}
          <p className="text-primary-900 font-normal text-md">{postMessage}</p>
        </div>
      )}
      {pollData && (
        <div className="mb-6 rounded-lg">
          {pollData?.length > 0 &&
            pollData?.map((option: PollOption, index: number) => (
              <div key={index} className="mb-2 last:mb-0">
                <div className="flex items-center justify-between mb-1">
                  <label
                    htmlFor={`option-${postId}-${index}`}
                    className="relative flex-grow px-4 py-3 border rounded-md cursor-pointer"
                    onClick={(event) => handlePollVote(event, option.option)}
                  >
                    <span className="relative z-10 ">{option.option}</span>
                    {(hasVoted || already_voted || isExpired) && (
                      <>
                        <div
                          className="absolute inset-0 bg-[#BDBDBD] rounded-md z-0"
                          style={{
                            width: `${
                              totalVotes
                                ? ((option.votes / totalVotes) * 100).toFixed(2)
                                : 0
                            }%`,
                            transition: "width 0.3s ease",
                          }}
                        ></div>
                        <span className="absolute z-10 right-2 top-2">
                          {totalVotes
                            ? ((option.votes / totalVotes) * 100).toFixed(2)
                            : 0}
                          %
                        </span>
                      </>
                    )}
                  </label>
                </div>
              </div>
            ))}
        </div>
      )}

      {attachments?.images?.length > 0 && extension !== "" && (
        <ImageContainer image={attachments.images[0]} extension={extension} />
      )}
      {attachments?.video && <VideoPlayer attachments={attachments} />}
      {attachments?.audio && (
        <div className={`w-full h-24 rounded-xl-10 mr-4 mb-4 overflow-hidden`}>
          <audio
            className="object-contain w-full h-full mx-auto rounded-xl-10"
            controls
          >
            <source src={attachments.audio} type="audio/mpeg" />
            Your browser does not support the audio tag.
          </audio>
        </div>
      )}
      {attachments?.file &&
        (isPDF(attachments.file) ? (
          <>
            <div>
              <PdfViewer
                pdfUrl={attachments.file}
                width="100%"
                height="400px"
              />
            </div>
          </>
        ) : (
          <div className="relative bg-[#E7ECFC] p-4 items-center justify-between w-[95%] rounded-lg shadow mb-4">
            <div className="flex items-center gap-3">
              <FileText />
              <div>
                <h2 className="text-lg font-medium">{fileDetails?.name}</h2>
                <p className="text-sm text-gray-500">
                  {fileDetails?.extension}{" "}
                </p>
              </div>
              <button className="absolute right-4 text-gray-600 ml-6">
                <a href={attachments.file} download>
                  <ArrowCircleDown />
                  <span className="sr-only">Download file</span>
                </a>
                <span className="sr-only">Download file</span>
              </button>
            </div>
          </div>
        ))}
      {!postData?.is_deleted ? (
        <div className="flex items-center justify-between mt-1 text-sm text-gray-500">
          <div className="flex space-x-4">
            <div className="flex items-center space-x-1 min-w-8">
              <div
                onClick={() => {
                  if (!isLiking) {
                    return handleLike();
                  }
                }}
                className="cursor-pointer"
              >
                <Heart
                  fillColor={likeData?.like ? "#FF5147" : "#ffffff"}
                  strokeColor={likeData?.like ? "#FF5147" : "#343330"}
                />
              </div>
              <span>{likeData?.count || 0}</span>
            </div>
            <div className="flex items-center space-x-1">
              <div
                className="cursor-pointer"
                onClick={() => setShowComment(!showComment)}
              >
                <ChatCentredText />
              </div>
              <span>{commentsCount || 0}</span>
            </div>
            {/* <button className="flex items-center space-x-1">
                        <PaperPlainTilt />
                        <span>60</span>
                    </button> */}
          </div>
          <button
            className="flex items-center space-x-1"
            onClick={handleBookmark}
          >
            <BookMark fillColor={isPinned ? "#404040" : "#FFFFFF"} />
          </button>
        </div>
      ) : null}

      <FeedComments
        showComment={showComment}
        postData={postData}
        handleCommets={handleCommets}
        deletedCommentInfo={deletedCommentInfo}
      />
    </div>
  );
};

export default FeedCard;
